<template>
  <div class="mw-100 overflow-y-auto flex-grow-1 container pt-4 pb-12">
    <div class="container">
      <!-- Header Section -->
      <div class="w-100 mt-4 mb-4">
        <div class="d-flex flex-column align-items-center mb-5">
          <h2 class="mb-0">{{ $t("dashboard.buyer.consolidated_view") }}</h2>
        </div>
      </div>

      <!-- Company Filter Section -->
      <div class="card border-translucent mb-4">
        <div
          class="card-body d-flex align-items-center justify-content-between flex-wrap gap-3"
        >
          <div class="d-flex align-items-center gap-3">
            <span
              class="company-filter-icon d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
            >
              <FontAwesomeIcon icon="layer-group" />
            </span>
            <div>
              <p class="mb-1 text-uppercase fw-semibold text-secondary small">
                {{ $t("common.companies") }}
              </p>
              <p class="mb-0 text-body-secondary">
                {{ $t("dashboard.consolidated.company_filter_hint") }}
              </p>
            </div>
          </div>
          <div
            class="filter-select flex-grow-1 flex-md-grow-0"
            style="min-width: 338px; max-width: 546px"
          >
            <MultiSelectComponent
              v-model="selectedCompanyIds"
              :options="companyOptions"
              :placeholder="$t('common.select_companies')"
              size="md"
            />
          </div>
        </div>
      </div>

      <!-- Main Card with Tabs -->
      <div class="card border-translucent">
        <div class="card-body">
          <TabsContainer
            :tabs="buyerTabs"
            @change:tab="handleTabChange"
            :activeTab="activeTab || 'buy'"
          >
            <template #tab-buy>
              <BuyAndCancelComponent
                v-if="companiesWithIds.length > 0 && companiesWithIds[0].id"
                :company-id="companiesWithIds[0].id"
              />
            </template>
            <template #tab-cancellation-documents>
              <CancellationStatementsComponent
                :companies="companiesWithIds"
                :is_admin="isAdmin || false"
              />
            </template>
            <template #tab-orders>
              <CompanyOrdersComponent
                :companies="companiesWithIds"
                :is_admin="isAdmin || false"
              />
            </template>
            <template #tab-transactions>
              <SalesTransactionsComponent
                :company-ids="companyIds"
                :companies="companiesWithIds"
                :nda_signed_link="ndaSignedLink"
              />
            </template>
            <template #tab-invoices>
              <InvoiceHistoryComponent
                :companies="companiesWithIds"
                :is_admin="isAdmin ?? false"
              />
            </template>
            <template #tab-settings>
              <SettingsComponent
                v-if="companiesWithIds.length > 0 && companiesWithIds[0].id"
                :companyId="companiesWithIds[0].id"
                :company="companiesWithIds[0]"
              />
            </template>
          </TabsContainer>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import TabsContainer from "@/components/common/tabs/TabsContainer.vue";
import { useAccount } from "@/composables/account/useAccount";
import { useTabs } from "@/composables/useTabs";
import BuyAndCancelComponent from "@/dashboard/inventory_management/bulk_buy/BulkBuyTableComponent.vue";
import CompanyOrdersComponent from "@/dashboard/inventory_management/company_orders/CompanyOrdersComponent.vue";
import CancellationStatementsComponent from "@/dashboard/invoice_management/CancellationStatementsComponent.vue";
import SettingsComponent from "@/dashboard/settings_management/SettingsComponent.vue";
import InvoiceHistoryComponent from "@/money/invoice/InvoiceHistoryComponent.vue";
import SalesTransactionsComponent from "@/money/SalesTransactionsComponent.vue";
import type { Company } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { filter } from "lodash-es";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const selectedCompanyIds = ref<number[]>([]);

const { accountData } = useAccount();

const buyerTabs = computed(() => [
  {
    id: "buy",
    name: t("common.actions.buy_and_cancel"),
    icon: "shopping-cart",
    disabled: false,
  },
  {
    id: "cancellation-documents",
    name: t("dashboard.companyData.cancellation_documents"),
    icon: "file-check",
    disabled: false,
  },
  {
    id: "orders",
    name: t("company_orders.title"),
    icon: "list-ul",
    disabled: false,
  },
  {
    id: "transactions",
    name: t("dashboard.sales_transactions.title"),
    icon: "exchange-alt",
    disabled: false,
  },
  {
    id: "invoices",
    name: t("dashboard.companyData.invoice_history"),
    icon: "history",
    disabled: false,
  },
  {
    id: "settings",
    name: t("settings.title"),
    icon: "cog",
    disabled: false,
  },
]);

const { activeTab, setTab: handleTabChange } = useTabs(
  "buyer-consolidated-overview",
  {
    defaultTabId: "buy",
    tabs: buyerTabs,
  }
);

const allCompaniesWithIds = computed(() => {
  return filter(
    accountData.companies,
    (company): company is Company & { id: number } =>
      typeof company.id === "number"
  );
});

const companyOptions = computed(() => {
  return allCompaniesWithIds.value.map((company) => ({
    label: company.name,
    value: company.id,
    description: company.country,
  }));
});

const companiesWithIds = computed(() => {
  if (selectedCompanyIds.value.length === 0) {
    return allCompaniesWithIds.value;
  }
  return allCompaniesWithIds.value.filter((company) =>
    selectedCompanyIds.value.includes(company.id)
  );
});

const companyIds = computed(() => {
  return companiesWithIds.value.map((company) => company.id);
});

const ndaSignedLink = computed(() => {
  if (companiesWithIds.value.length > 0) {
    return companiesWithIds.value[0].nda_signed_link || "";
  }
  return "";
});

const isAdmin = computed(() => {
  return !!accountData.admin_account_id;
});

// Initialize selectedCompanyIds with all company IDs when companies load
watch(
  () => accountData.companies,
  (companies) => {
    if (
      companies &&
      companies.length > 0 &&
      selectedCompanyIds.value.length === 0
    ) {
      selectedCompanyIds.value = allCompaniesWithIds.value.map(
        (company) => company.id
      );
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.company-filter-icon {
  width: 44px;
  height: 44px;
  font-size: 1.1rem;
}

.company-filter-icon svg {
  width: 1em;
  height: 1em;
}

.filter-select :deep(.float-root) {
  width: 100%;
}

.filter-select :deep(.form-control) {
  min-height: 44px;
}

@media (max-width: 768px) {
  .filter-select {
    width: 100%;
  }
}
</style>
